import { useState, useEffect, useRef } from 'react';
import { isValidMapPosition } from '../components/Dispatch/LiveDispatch/locationHelper';

/** Great-circle distance in meters. */
function haversineMeters(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Snap if GPS jumps (tunnel / bad fix); otherwise ease toward target each frame. */
const GPS_JUMP_SNAP_M = 500;

/**
 * Smoothly interpolates vehicle map position toward `targetCenter` for less jittery marker motion.
 * @param {{ lat: number, lng: number } | null} targetCenter
 * @returns {{ lat: number, lng: number } | null}
 */
export function useSmoothedVehiclePosition(targetCenter) {
  const posRef = useRef(null);
  const rafRef = useRef(null);
  const [display, setDisplay] = useState(null);

  useEffect(() => {
    if (!targetCenter || !isValidMapPosition(targetCenter)) return undefined;

    if (!posRef.current) {
      posRef.current = { lat: targetCenter.lat, lng: targetCenter.lng };
      setDisplay({ ...posRef.current });
      return undefined;
    }

    const distInit = haversineMeters(posRef.current, targetCenter);
    if (distInit > GPS_JUMP_SNAP_M) {
      posRef.current = { lat: targetCenter.lat, lng: targetCenter.lng };
      setDisplay({ ...posRef.current });
      return undefined;
    }

    const tick = () => {
      const cur = posRef.current;
      const tgt = targetCenter;
      if (!cur || !tgt || !isValidMapPosition(tgt)) return;
      const d = haversineMeters(cur, tgt);
      if (d < 2.5) {
        posRef.current = { lat: tgt.lat, lng: tgt.lng };
        setDisplay({ ...posRef.current });
        return;
      }
      const alpha = Math.min(0.42, Math.max(0.08, d / 130));
      const next = {
        lat: cur.lat + (tgt.lat - cur.lat) * alpha,
        lng: cur.lng + (tgt.lng - cur.lng) * alpha,
      };
      posRef.current = next;
      setDisplay(next);
      rafRef.current = requestAnimationFrame(tick);
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- interpolate toward GPS lat/lng only
  }, [targetCenter?.lat, targetCenter?.lng]);

  return display && isValidMapPosition(display) ? display : targetCenter;
}
